import React, { useMemo, useState } from "react";
import {
  Box, Button, Chip, Divider, Grid, IconButton, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, alpha,
  Dialog, DialogActions, DialogContent, DialogTitle, TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BoltIcon from "@mui/icons-material/Bolt";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import HistoryIcon from "@mui/icons-material/History";
import TerminalIcon from "@mui/icons-material/Terminal";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import {
  useGetDeviceQuery, useGetDeviceCommandsQuery, useGetDeviceHeartbeatsQuery,
  useGetDeviceScreenshotQuery, useGetSignageActivityQuery,
  useForceRefreshDeviceMutation, useDisableDeviceMutation, useEnableDeviceMutation,
  useQueueDeviceCommandMutation,
  useGetDeviceEventsQuery,
} from "../../../Api/attoDeskApi";
import {
  IDevice, IDeviceCommand, IDeviceEvent, IDeviceHeartbeat, ISignageActivity,
} from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";
import PageLoader from "../../../Components/Loader/PageLoader";
import {
  playerKind, playerTypeLabel, playerVersion, osLabel, browserLabel,
  screenLabel, orientationLabel, pixelRatioLabel, NOT_REPORTED,
} from "../../Device/deviceDisplay";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import {
  NAVY, GOLD, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";

const POLL_MS = 5_000;

const formatBytes = (b?: number | null): string => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1_048_576) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1_073_741_824) return `${(b / 1_048_576).toFixed(1)} MB`;
  return `${(b / 1_073_741_824).toFixed(2)} GB`;
};

const formatRelative = (iso?: string | null): string => {
  if (!iso) return "—";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const cmdColor = (status: IDeviceCommand["status"]) => {
  switch (status) {
    case "pending":   return { bg: "#fef3c7", fg: "#92400e" };
    case "sent":      return { bg: "#dbeafe", fg: "#1e40af" };
    case "completed": return { bg: "#dcfce7", fg: "#166534" };
    case "failed":    return { bg: "#fee2e2", fg: "#991b1b" };
  }
};

const Section: React.FC<{
  icon: React.ReactElement; title: string; accent: string; right?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, title, accent, right, children }) => (
  <Paper elevation={0} sx={{
    mb: 3, borderRadius: 3, border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: "#fff",
  }}>
    <Box sx={{
      background: `linear-gradient(135deg, ${alpha(accent, 0.04)} 0%, ${alpha(accent, 0.01)} 100%)`,
      borderBottom: `1px solid ${alpha(accent, 0.08)}`,
      px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5,
    }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: 1.5,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: `linear-gradient(135deg, ${alpha(accent, 0.14)} 0%, ${alpha(accent, 0.06)} 100%)`,
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 16, color: accent } })}
      </Box>
      <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 700, color: NAVY }}>{title}</Typography>
      {right}
    </Box>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Paper>
);

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: TEXT_SECONDARY,
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY }}>
      {value}
    </Typography>
  </Box>
);

const DeviceDetailMonitoring: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const deviceId = Number(id);
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const { data: deviceRes, isLoading, refetch: refetchDevice } = useGetDeviceQuery(deviceId, {
    pollingInterval: POLL_MS, refetchOnFocus: true,
  });
  const { data: hbRes } = useGetDeviceHeartbeatsQuery(deviceId, {
    pollingInterval: POLL_MS,
  });
  const { data: cmdRes, refetch: refetchCommands } = useGetDeviceCommandsQuery(deviceId, {
    pollingInterval: POLL_MS,
  });
  const { data: eventsRes } = useGetDeviceEventsQuery(deviceId, {
    pollingInterval: POLL_MS,
  });
  const { data: ssRes } = useGetDeviceScreenshotQuery(deviceId, {
    pollingInterval: 30_000,
  });
  const { data: activityRes } = useGetSignageActivityQuery({
    subject_type: "App\\Models\\Device",
    per_page: 50,
  });

  const [forceRefresh, { isLoading: isForcing }] = useForceRefreshDeviceMutation();
  const [disable, { isLoading: isDisabling }] = useDisableDeviceMutation();
  const [enable, { isLoading: isEnabling }] = useEnableDeviceMutation();
  const [queueCmd, { isLoading: isQueueing }] = useQueueDeviceCommandMutation();

  const [disableReason, setDisableReason] = useState("");
  const [disableOpen, setDisableOpen] = useState(false);

  const device: IDevice | null = useMemo(() => {
    const d = (deviceRes?.data as unknown) as IDevice | undefined;
    return d ?? null;
  }, [deviceRes]);

  const heartbeats: IDeviceHeartbeat[] = useMemo(
    () => (Array.isArray(hbRes?.data) ? (hbRes?.data as unknown as IDeviceHeartbeat[]) : []),
    [hbRes],
  );
  const commands: IDeviceCommand[] = useMemo(
    () => (Array.isArray(cmdRes?.data) ? (cmdRes?.data as unknown as IDeviceCommand[]) : []),
    [cmdRes],
  );
  const events: IDeviceEvent[] = useMemo(
    () => (Array.isArray(eventsRes?.data) ? (eventsRes?.data as unknown as IDeviceEvent[]) : []),
    [eventsRes],
  );
  const eventsSummary = (eventsRes as any)?.summary as
    | { restart_count: number; media_failures_7d: number; network_drops_24h: number }
    | undefined;
  const screenshotUrl: string | null = useMemo(() => {
    const d = (ssRes?.data as any);
    return d?.url ?? null;
  }, [ssRes]);

  const deviceActivity: ISignageActivity[] = useMemo(() => {
    if (!device) return [];
    const all = Array.isArray(activityRes?.data) ? (activityRes?.data as unknown as ISignageActivity[]) : [];
    return all.filter(a => a.subject_id === device.id).slice(0, 30);
  }, [activityRes, device]);

  if (isLoading || !device) return <PageLoader height="20vh" />;

  const runQueue = async (command: string) => {
    try {
      const res = await queueCmd({ id: deviceId, command }).unwrap();
      if (res.status) { showMessage(`Queued: ${command}`); refetchCommands(); }
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed to queue"); }
  };

  const runForce = async () => {
    try {
      const res = await forceRefresh(deviceId).unwrap();
      if (res.status) { showMessage("Force refresh queued"); refetchDevice(); }
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  const runDisable = async () => {
    try {
      const res = await disable({ id: deviceId, reason: disableReason || undefined }).unwrap();
      if (res.status) {
        showMessage("Device disabled");
        setDisableOpen(false); setDisableReason("");
        refetchDevice();
      }
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  const runEnable = async () => {
    try {
      const res = await enable(deviceId).unwrap();
      if (res.status) { showMessage("Device enabled"); refetchDevice(); }
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  const status = device.is_disabled ? "Disabled" : (device.status || "—");

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
      {/* Header */}
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        px: 3, py: 2, position: "sticky", top: 0, zIndex: 10,
        boxShadow: "0 4px 30px rgba(0,0,0,0.18)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={() => navigate("/monitoring/devices")} size="small"
            sx={{
              border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2,
              width: 38, height: 38, bgcolor: "rgba(255,255,255,0.05)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
            }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#e2e8f0" }} />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography fontSize={20} fontWeight={800} color="#fff">
                {device.device_code}
              </Typography>
              <Typography fontSize={14} color="rgba(255,255,255,0.65)">
                {device.device_name}
              </Typography>
              <Chip size="small" icon={<FiberManualRecordIcon sx={{ fontSize: 9 }} />}
                label={status.toUpperCase()}
                sx={{
                  height: 22, fontSize: 11, fontWeight: 700,
                  bgcolor: device.is_disabled ? "rgba(148,163,184,0.18)" : "rgba(16,185,129,0.18)",
                  color: device.is_disabled ? "#cbd5e1" : "#a7f3d0",
                  "& .MuiChip-icon": { color: device.is_disabled ? "#94a3b8" : "#10b981" },
                }} />
            </Box>
            <Typography fontSize={11.5} color="rgba(255,255,255,0.55)" mt={0.5}>
              {device.company?.name || "—"} · {device.location || "no location"} · v{device.playlist?.version ?? 0}
            </Typography>
          </Box>
          <Button variant="outlined" size="small"
            onClick={() => window.open(`/devices/${device.id}/print-sheet`, "_blank")}
            sx={{
              borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600,
              borderColor: "rgba(255,255,255,0.18)", color: "#e2e8f0",
              "&:hover": { borderColor: "rgba(255,255,255,0.35)", bgcolor: "rgba(255,255,255,0.06)" },
            }}>
            Print Setup Sheet
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* LEFT column */}
          <Grid item xs={12} lg={5}>
            {/* Device Summary */}
            <Section icon={<InfoOutlinedIcon />} title="Device Summary" accent="#3b82f6">
              <Grid container spacing={2}>
                <Grid item xs={6}><Field label="Code" value={device.device_code} /></Grid>
                <Grid item xs={6}><Field label="Name" value={device.device_name} /></Grid>
                <Grid item xs={6}><Field label="Player Type" value={(device.player_type || "—").toUpperCase()} /></Grid>
                <Grid item xs={6}><Field label="Installed App Version" value={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>{device.app_version || "Not reported"}{device.app_version_code != null ? ` (${device.app_version_code})` : ""}</span>
                    {device.update_status && (
                      <Chip size="small"
                        label={(device.update_status as string).replace(/_/g, " ").toUpperCase()}
                        sx={{
                          height: 18, fontSize: 9.5, fontWeight: 700,
                          bgcolor: device.update_status === "up_to_date" ? "#dcfce7"
                            : device.update_status === "update_available" ? "#dbeafe"
                            : device.update_status === "updating" ? "#fef3c7"
                            : device.update_status === "failed" ? "#fee2e2" : "#f1f5f9",
                          color: device.update_status === "up_to_date" ? "#166534"
                            : device.update_status === "update_available" ? "#1e40af"
                            : device.update_status === "updating" ? "#92400e"
                            : device.update_status === "failed" ? "#991b1b" : "#475569",
                        }} />
                    )}
                  </Box>
                } /></Grid>
                <Grid item xs={6}><Field label="Last App Updated" value={
                  device.app_updated_at
                    ? `${new Date(device.app_updated_at).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })} · ${formatRelative(device.app_updated_at)}`
                    : "Not reported"
                } /></Grid>
                <Grid item xs={6}><Field label="Assigned Release"
                  value={device.assigned_app_version || device.assigned_version || "—"} /></Grid>
                <Grid item xs={6}><Field label="Last Update Attempt"
                  value={device.last_update_attempt_at ? formatRelative(device.last_update_attempt_at) : "—"} /></Grid>
                {device.last_update_error && (
                  <Grid item xs={12}>
                    <Field label="Last Update Error"
                      value={<span style={{ color: "#b91c1c" }}>{device.last_update_error}</span>} />
                  </Grid>
                )}
                <Grid item xs={6}><Field label="Last Seen" value={formatRelative(device.last_seen_at)} /></Grid>
                <Grid item xs={6}><Field label="Last Sync" value={formatRelative(device.last_sync_at)} /></Grid>
                <Grid item xs={12}>
                  <Field label="Storage"
                    value={device.storage_total
                      ? `${formatBytes(device.storage_used)} / ${formatBytes(device.storage_total)}`
                      : "—"} />
                </Grid>
                <Grid item xs={12}>
                  <Field label="Health" value={(device.health_status || "—").toUpperCase()} />
                </Grid>
                {device.last_error && (
                  <Grid item xs={12}>
                    <Field label="Last Error"
                      value={<span style={{ color: "#b91c1c" }}>{device.last_error}</span>} />
                  </Grid>
                )}
              </Grid>
            </Section>

            {/* Player Information — richer runtime/technical detail. Browser
                fields (OS / browser / screen) show only for web players; an
                Android device just shows its player type + version. Missing
                values render as "Not reported"/"Unknown", never null/0x0. */}
            <Section icon={<DevicesOtherIcon />} title="Player Information" accent="#0e7c9b">
              <Grid container spacing={2}>
                <Grid item xs={6}><Field label="Player Type" value={playerTypeLabel(device)} /></Grid>
                <Grid item xs={6}><Field label="Player Version" value={playerVersion(device) || NOT_REPORTED} /></Grid>
                {playerKind(device) === "web" && (
                  <>
                    <Grid item xs={6}><Field label="Operating System" value={osLabel(device)} /></Grid>
                    <Grid item xs={6}><Field label="Browser" value={browserLabel(device)} /></Grid>
                    <Grid item xs={6}><Field label="Resolution" value={screenLabel(device) || NOT_REPORTED} /></Grid>
                    <Grid item xs={6}><Field label="Orientation" value={orientationLabel(device)} /></Grid>
                    <Grid item xs={6}><Field label="Pixel Ratio" value={pixelRatioLabel(device)} /></Grid>
                  </>
                )}
                <Grid item xs={6}><Field label="Last Seen" value={formatRelative(device.last_seen_at)} /></Grid>
                <Grid item xs={6}><Field label="Metadata Updated"
                  value={device.metadata_updated_at ? formatRelative(device.metadata_updated_at) : NOT_REPORTED} /></Grid>
                {device.user_agent && (
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: TEXT_SECONDARY,
                      textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
                      User Agent
                    </Typography>
                    <Tooltip title={device.user_agent}>
                      <Typography sx={{
                        fontSize: 11, color: TEXT_SECONDARY, fontFamily: "monospace",
                        wordBreak: "break-all", lineHeight: 1.4,
                      }}>
                        {device.user_agent}
                      </Typography>
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            </Section>

            {/* Current Playlist */}
            <Section icon={<PermMediaIcon />} title="Current Playlist" accent="#7c3aed">
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field label="Playlist Group"
                    value={device.playlist_group?.name || device.playlist?.name || "Not assigned"} />
                </Grid>
                <Grid item xs={6}>
                  <Field label="Playback Mode"
                    value={device.playlist_group?.playback_mode || "—"} />
                </Grid>
                <Grid item xs={6}>
                  <Field label="Version" value={device.playlist?.version ?? "—"} />
                </Grid>
                <Grid item xs={12}>
                  <Field label="Currently Playing"
                    value={device.current_media_name || "—"} />
                </Grid>
              </Grid>
            </Section>

            {/* Latest Screenshot */}
            <Section icon={<PhotoCameraIcon />} title="Latest Screenshot" accent="#0e7c9b"
              right={
                <Button size="small" startIcon={<PhotoCameraIcon sx={{ fontSize: 14 }} />}
                  onClick={() => runQueue("request_screenshot")}
                  disabled={isQueueing || device.is_disabled}
                  sx={{ textTransform: "none", borderRadius: 2, fontSize: 12, fontWeight: 600,
                    color: "#0e7c9b" }}>
                  Request
                </Button>
              }>
              {screenshotUrl ? (
                <Box component="img" src={screenshotUrl} alt="latest"
                  sx={{ width: "100%", borderRadius: 2, border: `1px solid ${BORDER}`,
                    bgcolor: "#000", display: "block" }} />
              ) : (
                <Box sx={{ py: 4, textAlign: "center", border: `2px dashed ${BORDER}`,
                  borderRadius: 3, bgcolor: "#f8fafc", color: TEXT_SECONDARY, fontSize: 12 }}>
                  No screenshot yet. Click "Request" to ask the device to upload one.
                </Box>
              )}
            </Section>
          </Grid>

          {/* RIGHT column */}
          <Grid item xs={12} lg={7}>
            {/* Remote Actions */}
            <Section icon={<TerminalIcon />} title="Remote Actions" accent="#f59e0b">
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}>
                <ActionTile icon={<BoltIcon />} label="Force Refresh"
                  color="#f59e0b" disabled={isForcing || device.is_disabled} onClick={runForce} />
                {device.is_disabled ? (
                  <ActionTile icon={<CheckCircleIcon />} label="Enable Device"
                    color="#10b981" disabled={isEnabling} onClick={runEnable} />
                ) : (
                  <ActionTile icon={<BlockIcon />} label="Disable Device"
                    color="#dc2626" disabled={isDisabling} onClick={() => setDisableOpen(true)} />
                )}
                <ActionTile icon={<CleaningServicesIcon />} label="Clear Cache"
                  color="#3b82f6" disabled={isQueueing || device.is_disabled}
                  onClick={() => runQueue("clear_cache")} />
                <ActionTile icon={<RestartAltIcon />} label="Restart Player"
                  color="#7c3aed" disabled={isQueueing || device.is_disabled}
                  onClick={() => runQueue("restart_player")} />
                <ActionTile icon={<PhotoCameraIcon />} label="Request Screenshot"
                  color="#0e7c9b" disabled={isQueueing || device.is_disabled}
                  onClick={() => runQueue("request_screenshot")} />
                <ActionTile icon={<RestartAltIcon />} label="Update Now"
                  color="#0ea5e9" disabled={isQueueing || device.is_disabled}
                  onClick={() => runQueue("install_update")} />
              </Box>
              <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 2 }}>
                Clear Cache, Restart Player, Request Screenshot, and Update Now are queued — the player executes them on its next heartbeat.
              </Typography>
            </Section>

            {/* Command Queue */}
            <Section icon={<TerminalIcon />} title={`Commands (${commands.length})`} accent="#475569">
              {commands.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, textAlign: "center", py: 3 }}>
                  No commands sent yet.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {["Command", "Status", "Queued", "Sent", "Executed", "Result"].map(h => (
                          <TableCell key={h} sx={{ fontSize: 10.5, fontWeight: 700,
                            color: TEXT_SECONDARY, textTransform: "uppercase",
                            letterSpacing: "0.05em" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {commands.map(c => {
                        const col = cmdColor(c.status);
                        return (
                          <TableRow key={c.id}>
                            <TableCell sx={{ fontFamily: "monospace", fontSize: 12, fontWeight: 600 }}>
                              {c.command}
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={c.status.toUpperCase()}
                                sx={{ height: 20, fontSize: 10, fontWeight: 700,
                                  bgcolor: col.bg, color: col.fg }} />
                            </TableCell>
                            <TableCell sx={{ fontSize: 11, color: TEXT_PRIMARY }}>
                              {formatRelative(c.created_at)}
                            </TableCell>
                            <TableCell sx={{ fontSize: 11, color: TEXT_PRIMARY }}>
                              {formatRelative(c.sent_at)}
                            </TableCell>
                            <TableCell sx={{ fontSize: 11, color: TEXT_PRIMARY }}>
                              {formatRelative(c.executed_at)}
                            </TableCell>
                            <TableCell sx={{ fontSize: 11, color: TEXT_SECONDARY, maxWidth: 200 }}>
                              <Typography noWrap sx={{ fontSize: 11 }}>{c.result_message || "—"}</Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>

            {/* Heartbeat History */}
            <Section icon={<HistoryIcon />} title={`Heartbeats (last ${heartbeats.length})`} accent="#10b981">
              {heartbeats.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, textAlign: "center", py: 3 }}>
                  No heartbeats received yet.
                </Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {["When", "Version", "App", "Player", "Storage", "Media", "IP"].map(h => (
                          <TableCell key={h} sx={{ fontSize: 10.5, fontWeight: 700,
                            color: TEXT_SECONDARY, textTransform: "uppercase",
                            letterSpacing: "0.05em", bgcolor: "#fff" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {heartbeats.map(h => (
                        <TableRow key={h.id} hover>
                          <TableCell sx={{ fontSize: 11, whiteSpace: "nowrap" }}>
                            <Tooltip title={h.created_at || ""}>
                              <span>{formatRelative(h.created_at)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{h.playlist_version ?? "—"}</TableCell>
                          <TableCell sx={{ fontSize: 11, fontFamily: "monospace" }}>{h.app_version || "—"}</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{h.player_type || "—"}</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>
                            {h.storage_total
                              ? `${formatBytes(h.storage_used)} / ${formatBytes(h.storage_total)}`
                              : "—"}
                          </TableCell>
                          <TableCell sx={{ fontSize: 11, maxWidth: 200 }}>
                            <Typography noWrap sx={{ fontSize: 11 }}>{h.current_media_name || "—"}</Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: 11, fontFamily: "monospace", color: TEXT_SECONDARY }}>
                            {h.ip_address || "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>

            {/* Performance Summary + Events */}
            <Section icon={<HistoryIcon />} title="Stability" accent="#3b82f6">
              {eventsSummary ? (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <MetricTile label="Restarts (7d)"
                      value={eventsSummary.restart_count}
                      color={eventsSummary.restart_count > 3 ? "#dc2626" : "#10b981"} />
                  </Grid>
                  <Grid item xs={4}>
                    <MetricTile label="Media failures (7d)"
                      value={eventsSummary.media_failures_7d}
                      color={eventsSummary.media_failures_7d > 0 ? "#f59e0b" : "#10b981"} />
                  </Grid>
                  <Grid item xs={4}>
                    <MetricTile label="Network drops (24h)"
                      value={eventsSummary.network_drops_24h}
                      color={eventsSummary.network_drops_24h > 0 ? "#f59e0b" : "#10b981"} />
                  </Grid>
                </Grid>
              ) : null}

              {events.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, textAlign: "center", py: 3 }}>
                  No events recorded yet. Players report breadcrumbs as they play.
                </Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {["When", "Event", "Message"].map(h => (
                          <TableCell key={h} sx={{
                            fontSize: 10.5, fontWeight: 700, color: TEXT_SECONDARY,
                            textTransform: "uppercase", letterSpacing: "0.05em", bgcolor: "#fff",
                          }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {events.map(e => {
                        const isErr = ["media_failed", "network_lost", "app_crash_recovered"].includes(e.event_type);
                        const isRecov = ["player_recovered", "network_restored"].includes(e.event_type);
                        return (
                          <TableRow key={e.id} hover>
                            <TableCell sx={{ fontSize: 11, whiteSpace: "nowrap" }}>
                              <Tooltip title={e.created_at || ""}>
                                <span>{formatRelative(e.created_at)}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={e.event_type}
                                sx={{
                                  height: 20, fontSize: 10, fontWeight: 700,
                                  bgcolor: isErr ? "#fee2e2" : isRecov ? "#dcfce7" : "#dbeafe",
                                  color: isErr ? "#991b1b" : isRecov ? "#166534" : "#1e40af",
                                  fontFamily: "monospace",
                                }} />
                            </TableCell>
                            <TableCell sx={{ fontSize: 12, color: TEXT_PRIMARY, maxWidth: 320 }}>
                              <Typography noWrap sx={{ fontSize: 12 }}>{e.message || "—"}</Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>

            {/* Activity Log */}
            <Section icon={<HistoryIcon />} title={`Activity (${deviceActivity.length})`} accent="#64748b">
              {deviceActivity.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, textAlign: "center", py: 3 }}>
                  No activity recorded for this device yet.
                </Typography>
              ) : (
                <Box>
                  {deviceActivity.map(a => (
                    <Box key={a.id} sx={{ py: 1.25, borderBottom: `1px solid ${BORDER}`,
                      "&:last-child": { borderBottom: "none" } }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Chip size="small" label={a.action}
                          sx={{ height: 20, fontSize: 10, fontWeight: 700,
                            bgcolor: "#f1f5f9", color: NAVY, fontFamily: "monospace" }} />
                        <Typography sx={{ fontSize: 12, color: TEXT_PRIMARY }}>
                          {a.user?.name || "system"}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
                          {formatRelative(a.created_at)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Section>
          </Grid>
        </Grid>
      </Box>

      {/* Disable dialog */}
      <Dialog open={disableOpen} onClose={() => setDisableOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800, color: NAVY }}>Disable Device</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mb: 2 }}>
            Disabling stops the player immediately. The device will show "Device Disabled" until re-enabled.
          </Typography>
          <TextField fullWidth size="small" label="Reason (optional)"
            value={disableReason} onChange={(e) => setDisableReason(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDisableOpen(false)} sx={{ textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation color="error" onClick={runDisable}
            disabled={isDisabling} sx={{ textTransform: "none", fontWeight: 700 }}>
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const MetricTile: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: "#fff", textAlign: "center" }}>
    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: TEXT_SECONDARY,
      textTransform: "uppercase", letterSpacing: "0.06em" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 24, fontWeight: 800, color }}>
      {value}
    </Typography>
  </Paper>
);

const ActionTile: React.FC<{
  icon: React.ReactElement; label: string; color: string;
  disabled?: boolean; onClick: () => void;
}> = ({ icon, label, color, disabled, onClick }) => (
  <Button onClick={onClick} disabled={disabled}
    sx={{
      flexDirection: "column", gap: 0.5, py: 2, borderRadius: 2,
      border: `1px solid ${BORDER}`, bgcolor: "#fff",
      textTransform: "none", color: NAVY, fontWeight: 600, fontSize: 12,
      "&:hover": { borderColor: color, bgcolor: alpha(color, 0.04), color },
      "&.Mui-disabled": { opacity: 0.5 },
    }}>
    {React.cloneElement(icon, { sx: { fontSize: 22, color } })}
    {label}
  </Button>
);

export default DeviceDetailMonitoring;
